import React from "react";

import Button from "@SonarCX/components/atoms/Button2";
import ButtonSecondary from "@SonarCX/components/atoms/ButtonSecondary";
import Dropdown from "@SonarCX/components/atoms/Dropdown";
import MultiDropdown from "@SonarCX/components/atoms/MultiselectDropdown";
import Spacer from "@SonarCX/components/atoms/Spacer";
import BaseModal from "@SonarCX/components/molecules/ChatModals/BaseModal";
import { useAppSelector } from "@SonarCX/hooks/redux";
import { useSupabase } from "@SonarCX/providers/SupabaseProvider";
import { selectCategory } from "@SonarCX/slices/category";
import { selectTaskType } from "@SonarCX/slices/taskType";
import useEditStyles from "../../../SettingsEditModal/styles";
import useStyles from "./styles";

type CategoryRulesSettingsModalProps = {
  values?: {
    task_category_id: string;
    task_type_ids: string[];
  };
  onClose: () => void;
  onSubmit: (category: string, types: string[]) => Promise<void>;
};

const CategoryRulesSettingsModal: React.FC<CategoryRulesSettingsModalProps> = ({
  values,
  onClose,
  onSubmit,
}) => {
  const {
    taskCategoryRules: { rules },
  } = useSupabase();
  const { categories, categoriesValues } = useAppSelector(selectCategory);
  const { types, typesValues } = useAppSelector(selectTaskType);

  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(
    values?.task_category_id ?? null
  );
  const [selectedTypes, setSelectedTypes] = React.useState<string[]>(
    values?.task_type_ids ?? []
  );
  const [saving, setSaving] = React.useState(false);

  const namedTypes = React.useMemo(() => {
    return selectedTypes.map((id) => types[id]?.name).filter(Boolean);
  }, [selectedTypes, types]);

  const isEditing = React.useMemo(() => Boolean(values), [values]);

  const [entriesValid, reason] = React.useMemo(() => {
    const nothingSelected =
      selectedCategory === null || selectedTypes.length === 0;
    if (nothingSelected) {
      return [false, "Please select a category and at least one type."];
    }

    if (rules.some((rule) => rule.task_category_id === selectedCategory)) {
      if (isEditing && values?.task_category_id === selectedCategory) {
        return [true, ""];
      }

      return [false, "A rule for this category already exists."];
    }

    return [true, ""];
  }, [selectedCategory, selectedTypes, rules, isEditing, values]);

  const editClasses = useEditStyles();
  const classes = useStyles();

  const handleSubmit = React.useCallback(async () => {
    setSaving(true);
    await onSubmit(selectedCategory!, selectedTypes);
    setSaving(false);
    onClose();
  }, [onSubmit, onClose, selectedCategory, selectedTypes]);

  return (
    // @ts-expect-error prop types incorrect
    <BaseModal
      open
      onClose={onClose}
      PaperProps={{ style: { top: "53%", width: "50%" } }}
      title={`${values ? "Edit" : "Add"} Category Rule`}
    >
      <div className={classes.root}>
        <div className={classes.controlRow}>
          <span className={classes.label}>Category</span>

          <Dropdown
            options={categoriesValues.map(({ id }) => id)}
            renderOption={(id) => {
              const opt = categories[id];
              if (opt) {
                return <div key={opt.id}>{opt.name}</div>;
              } else {
                return "";
              }
            }}
            selected={selectedCategory}
            onSelect={(val) => setSelectedCategory(val as string)}
            hideAdd
            duplicates
            disablePortal
          >
            {(() => {
              const category = categories[selectedCategory];
              return (
                <div className={classes.dropdown}>
                  {category?.name || "No Category Selected"}
                </div>
              );
            })()}
          </Dropdown>
        </div>

        <div className={classes.controlRow}>
          <span className={classes.label}>Types</span>

          <MultiDropdown
            options={typesValues.map(({ id }) => id)}
            renderOption={(id) => types[id]?.name ?? "Unknown"}
            selected={selectedTypes}
            onSelect={(id, isSelected) => {
              setSelectedTypes((prev) => {
                if (isSelected) {
                  return [...prev, id];
                } else {
                  return prev.filter((typeId) => typeId !== id);
                }
              });
            }}
          >
            <div className={classes.dropdown}>
              {selectedTypes.length === 0 ? (
                "No Types Selected"
              ) : (
                <div className={classes.selectedTypes}>
                  {namedTypes.join(", ")}
                </div>
              )}
            </div>
          </MultiDropdown>
        </div>

        <span className={classes.infoText}>
          When the selected category is chosen for a task, the type options will
          be limited to the chosen types.
        </span>
      </div>

      {!entriesValid && <div className={classes.warningText}>{reason}</div>}

      <div className={editClasses.controls}>
        <Spacer />

        <div className={editClasses.normalControls}>
          <ButtonSecondary onClick={onClose} className={editClasses.button}>
            CANCEL
          </ButtonSecondary>

          <Button
            disabled={!entriesValid || saving}
            onClick={handleSubmit}
            className={editClasses.button}
          >
            SAVE
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

export default CategoryRulesSettingsModal;
